<template>
  <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header: project picker + color-mode toggle -->
    <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">Project Timeline</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Gantt timeline of sprints and their tickets across the project.</p>
      </div>
      <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div class="sm:flex-1">
          <v-autocomplete density="compact" variant="solo-filled" flat hide-details label="Project" :items="projects" item-title="title" item-value="id" v-model="selectedProject" @update:modelValue="loadGantt"></v-autocomplete>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-gray-500 dark:text-gray-400">Color by</span>
          <div class="inline-flex rounded-lg bg-gray-100 p-0.5 text-xs font-semibold dark:bg-gray-800">
            <button type="button" @click="colorMode = 'status'" class="rounded-md px-3 py-1 transition" :class="colorMode === 'status' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">Status</button>
            <button type="button" @click="colorMode = 'priority'" class="rounded-md px-3 py-1 transition" :class="colorMode === 'priority' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50' : 'text-gray-500 dark:text-gray-400'">Priority</button>
          </div>
        </div>
      </div>

      <!-- Legend + summary -->
      <div class="mt-3 flex flex-wrap items-center gap-3">
        <div class="flex flex-wrap items-center gap-2.5">
          <span v-for="l in legend" :key="l.label" class="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span class="h-3 w-3 rounded" :style="{ background: l.color }"></span>{{ l.label }}
          </span>
        </div>
        <div v-if="!loading && (sprints.length || tickets.length)" class="ml-auto flex items-center gap-1.5">
          <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300"><v-icon size="13">mdi-run-fast</v-icon>{{ sprints.length }} sprints</span>
          <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300"><v-icon size="13">mdi-ticket-outline</v-icon>{{ tickets.length }} tickets</span>
          <span v-if="overdueCount" class="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300"><v-icon size="13">mdi-alert-circle-outline</v-icon>{{ overdueCount }} overdue</span>
        </div>
      </div>
    </div>

    <!-- Schedule verdict banner: within / at risk / beyond the set timeline -->
    <div
      v-if="!loading && selectedProject"
      class="rounded-2xl border p-3"
      :class="{
        grey: 'border-slate-200 bg-slate-50 dark:border-slate-500/30 dark:bg-slate-500/10',
        error: 'border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10',
        warning: 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10',
        success: 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10',
      }[schedule.color]"
    >
      <div class="flex flex-wrap items-center gap-2">
        <v-icon :color="schedule.color" size="20">{{ schedule.icon }}</v-icon>
        <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ schedule.message }}</span>
        <span v-if="project && (project.start_date || project.end_date)" class="ml-auto text-xs text-gray-500 dark:text-gray-400">
          Baseline: {{ project.start_date || '—' }} → {{ project.end_date || '—' }}
        </span>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

      <EmptyState
        v-if="!loading && !hasTimeline"
        icon="mdi-chart-timeline"
        title="Nothing to chart yet"
        subtitle="This project has no sprints or dated tickets to plot."
      />

      <div v-else-if="!loading" class="gantt-scroll">
        <div
          class="gantt-inner"
          :style="{
            '--label': labelWidth + 'px',
            '--day': dayWidth + 'px',
            '--track': trackWidth + 'px',
            '--week': (dayWidth * 7) + 'px',
          }"
        >
          <!-- Today marker spanning the whole chart -->
          <div v-if="todayLeft !== null" class="g-today-line" :style="{ left: (labelWidth + todayLeft) + 'px' }">
            <span class="g-today-flag">Today</span>
          </div>

          <!-- Target end (deadline) marker -->
          <div v-if="targetLeft !== null" class="g-target-line" :style="{ left: (labelWidth + targetLeft) + 'px' }">
            <span class="g-target-flag">Target</span>
          </div>

          <!-- Month header -->
          <div class="g-row g-head">
            <div class="g-label g-corner">Sprint / Ticket</div>
            <div class="g-track g-monthbar">
              <div
                v-for="m in monthBlocks"
                :key="m.key"
                class="g-month"
                :style="{ left: m.left + 'px', width: m.width + 'px' }"
              >{{ m.label }}</div>
            </div>
          </div>

          <!-- Project baseline (planned timeline) -->
          <div v-if="project" class="g-row g-baselinerow">
            <div class="g-label g-baselinelabel">
              <v-icon size="14" class="mr-1">mdi-flag-checkered</v-icon>
              <span class="text-truncate">Project timeline</span>
            </div>
            <div class="g-track">
              <div
                v-if="baselineBar"
                class="g-bar g-baselinebar"
                :style="{ left: baselineBar.left + 'px', width: baselineBar.width + 'px' }"
                :title="baselineTitle()"
              >
                <span class="g-bar-label">Planned</span>
              </div>
              <div v-else class="g-nodate">no timeline set — set it on the Projects page</div>
            </div>
          </div>

          <!-- Sprint groups -->
          <template v-for="grp in groups" :key="grp.sprint.id">
            <div class="g-row g-sprintrow">
              <div class="g-label g-sprintlabel" :title="grp.sprint.name">
                <v-icon size="14" class="mr-1">mdi-run-fast</v-icon>
                <span class="text-truncate">{{ grp.sprint.name }}</span>
                <v-chip size="x-small" class="ml-1" :color="sprintColor(grp.sprint.status)" variant="flat">{{ grp.sprint.status }}</v-chip>
              </div>
              <div class="g-track">
                <div
                  v-if="grp.sprintBar"
                  class="g-bar g-sprintbar"
                  :style="{ left: grp.sprintBar.left + 'px', width: grp.sprintBar.width + 'px', background: sprintColor(grp.sprint.status) }"
                  :title="sprintTitle(grp.sprint)"
                >
                  <span class="g-bar-label">{{ grp.sprint.name }}</span>
                </div>
                <div v-else class="g-nodate">no dates set</div>
              </div>
            </div>

            <div v-for="t in grp.tickets" :key="t.id" class="g-row g-ticketrow">
              <div class="g-label g-ticketlabel" :title="t.name">
                <span class="g-tid">{{ t.ticket_id }}</span>
                <span class="text-truncate">{{ t.name }}</span>
              </div>
              <div class="g-track">
                <div
                  class="g-bar"
                  :class="{ 'g-overdue': isOverdue(t) }"
                  :style="{ left: barFor(t).left + 'px', width: barFor(t).width + 'px', background: barColor(t) }"
                  :title="ticketTitle(t)"
                >
                  <span class="g-bar-label">{{ t.name }}</span>
                  <v-icon v-if="isOverdue(t)" size="11" class="g-overdue-ic">mdi-alert-circle</v-icon>
                </div>
              </div>
            </div>

            <div v-if="grp.tickets.length === 0" class="g-row g-ticketrow">
              <div class="g-label g-ticketlabel g-empty-sub">No tickets in this sprint</div>
              <div class="g-track"></div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import utils from '@/utils';
import constant from '@/constant';
import apiMixin from '@/mixins/apiMixin';
import formatMixin from '@/mixins/formatMixin';
import EmptyState from '@/components/ui/EmptyState.vue';
import Snackbar from './Snackbar.vue';

const DAY_MS = 86400000;
const STATUS_COLORS = {
  'Todo': '#9aa0aa',
  'In Progress': '#2c7be5',
  'On Hold': '#f5a623',
  'Cancelled': '#e5484d',
  'Done': '#2e9e5b',
};
const PRIORITY_COLORS = {
  lowest: '#9aa0aa',
  low: '#2c7be5',
  medium: '#f5b301',
  high: '#f5733a',
  critical: '#e5484d',
};

export default {
  name: 'GanttPage',
  components: { EmptyState, Snackbar },
  mixins: [apiMixin, formatMixin],
  data() {
    return {
      constant,
      projects: [],
      selectedProject: null,
      project: null,
      sprints: [],
      tickets: [],
      loading: false,
      colorMode: 'status',
      dayWidth: 30,
      labelWidth: 240,
      isshow: false,
      message: '',
    };
  },
  computed: {
    // Local midnight Date for the earliest/latest points we need to plot,
    // padded a couple of days on each side, always including "today".
    range() {
      const dates = [];
      const add = (s) => { const d = this.toDate(s); if (d) dates.push(d.getTime()); };
      if (this.project) { add(this.project.start_date); add(this.project.end_date); }
      this.sprints.forEach((s) => { add(s.start_date); add(s.end_date); });
      this.tickets.forEach((t) => { add(t.created_at); add(t.due_date); });
      dates.push(this.midnight(new Date()).getTime());
      if (!dates.length) return null;
      const start = new Date(Math.min(...dates) - 2 * DAY_MS);
      const end = new Date(Math.max(...dates) + 2 * DAY_MS);
      return { start: this.midnight(start), end: this.midnight(end) };
    },
    totalDays() {
      if (!this.range) return 0;
      return Math.round((this.range.end - this.range.start) / DAY_MS) + 1;
    },
    trackWidth() {
      return this.totalDays * this.dayWidth;
    },
    hasTimeline() {
      return !!this.range && (this.sprints.length > 0 || this.tickets.length > 0);
    },
    // Sprints with their computed bar + child tickets (sorted by start).
    groups() {
      const bySprint = {};
      this.tickets.forEach((t) => {
        (bySprint[t.sprint_id] = bySprint[t.sprint_id] || []).push(t);
      });
      return this.sprints.map((sprint) => {
        const tickets = (bySprint[sprint.id] || []).slice().sort((a, b) => {
          const da = this.toDate(a.created_at), db = this.toDate(b.created_at);
          return (da ? da.getTime() : 0) - (db ? db.getTime() : 0);
        });
        return { sprint, sprintBar: this.barBetween(sprint.start_date, sprint.end_date), tickets };
      });
    },
    monthBlocks() {
      if (!this.range) return [];
      const blocks = [];
      const cur = new Date(this.range.start.getTime());
      cur.setDate(1);
      while (cur <= this.range.end) {
        const monthStart = new Date(cur.getTime());
        const next = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
        const visibleStart = monthStart < this.range.start ? this.range.start : monthStart;
        const visibleEnd = next > this.range.end ? new Date(this.range.end.getTime() + DAY_MS) : next;
        const left = this.dayIndex(visibleStart) * this.dayWidth;
        const width = Math.max(0, Math.round((visibleEnd - visibleStart) / DAY_MS) * this.dayWidth);
        blocks.push({
          key: `${monthStart.getFullYear()}-${monthStart.getMonth()}`,
          label: monthStart.toLocaleString('default', { month: 'short', year: 'numeric' }),
          left,
          width,
        });
        cur.setMonth(cur.getMonth() + 1);
      }
      return blocks;
    },
    todayLeft() {
      if (!this.range) return null;
      const t = this.midnight(new Date());
      if (t < this.range.start || t > this.range.end) return null;
      return this.dayIndex(t) * this.dayWidth + this.dayWidth / 2;
    },
    overdueCount() {
      return this.tickets.filter((t) => this.isOverdue(t)).length;
    },
    // Planned project baseline bar (start -> target end).
    baselineBar() {
      if (!this.project || (!this.project.start_date && !this.project.end_date)) return null;
      return this.barBetween(this.project.start_date, this.project.end_date);
    },
    // Vertical marker at the target end date, when in range.
    targetLeft() {
      if (!this.range || !this.project || !this.project.end_date) return null;
      const d = this.toDate(this.project.end_date);
      if (!d || d < this.range.start || d > this.range.end) return null;
      return this.dayIndex(d) * this.dayWidth + this.dayWidth / 2;
    },
    // Latest date any work is expected to finish (max sprint end / ticket due).
    forecastEnd() {
      let max = null;
      const consider = (s) => {
        const d = this.toDate(s);
        if (d && (!max || d > max)) max = d;
      };
      this.sprints.forEach((s) => consider(s.end_date));
      this.tickets.forEach((t) => consider(t.due_date));
      return max;
    },
    // Schedule verdict against the set target end: within / at risk / beyond.
    schedule() {
      const end = this.project ? this.toDate(this.project.end_date) : null;
      if (!end) {
        return {
          state: 'none',
          color: 'grey',
          icon: 'mdi-calendar-alert',
          message: 'No project timeline set — set a planned start and target end on the Projects page to monitor schedule.',
        };
      }
      const today = this.midnight(new Date());
      const dayDiff = (a, b) => Math.round((a - b) / DAY_MS);
      const fc = this.forecastEnd;

      if (today > end) {
        return {
          state: 'beyond',
          color: 'error',
          icon: 'mdi-alert-circle',
          message: `Beyond timeline — ${dayDiff(today, end)} day(s) past the target end (${this.formattedDate(this.project.end_date)}).`,
        };
      }
      if (fc && fc > end) {
        return {
          state: 'atrisk',
          color: 'warning',
          icon: 'mdi-alert',
          message: `At risk — work is forecast to finish ${this.formattedDate(this.dateStr(fc))}, ${dayDiff(fc, end)} day(s) past the target end (${this.formattedDate(this.project.end_date)}).`,
        };
      }
      return {
        state: 'within',
        color: 'success',
        icon: 'mdi-check-circle',
        message: `On track — ${dayDiff(end, today)} day(s) remaining until the target end (${this.formattedDate(this.project.end_date)}).`,
      };
    },
    legend() {
      if (this.colorMode === 'priority') {
        return [
          { label: 'Lowest', color: PRIORITY_COLORS.lowest },
          { label: 'Low', color: PRIORITY_COLORS.low },
          { label: 'Medium', color: PRIORITY_COLORS.medium },
          { label: 'High', color: PRIORITY_COLORS.high },
          { label: 'Critical', color: PRIORITY_COLORS.critical },
        ];
      }
      return Object.keys(STATUS_COLORS).map((k) => ({ label: k, color: STATUS_COLORS[k] }));
    },
  },
  methods: {
    notify(msg) {
      this.message = msg;
      this.isshow = true;
      setTimeout(() => { this.isshow = false; }, 1500);
    },
    // ---- date helpers (all local-midnight to avoid TZ drift) ----
    midnight(d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },
    toDate(str) {
      if (!str) return null;
      const parts = String(str).slice(0, 10).split('-');
      if (parts.length !== 3) return null;
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return isNaN(d) ? null : d;
    },
    dateStr(d) {
      if (!d) return '';
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${m}-${day}`;
    },
    dayIndex(d) {
      if (!this.range) return 0;
      return Math.round((this.midnight(d) - this.range.start) / DAY_MS);
    },
    // Bar geometry between two date strings; null when neither date exists.
    barBetween(startStr, endStr) {
      let s = this.toDate(startStr);
      let e = this.toDate(endStr);
      if (!s && !e) return null;
      if (!s) s = e;
      if (!e) e = s;
      if (e < s) { const tmp = s; s = e; e = tmp; }
      const si = this.dayIndex(s);
      const ei = this.dayIndex(e);
      const left = si * this.dayWidth;
      const width = Math.max(this.dayWidth * 0.9, (ei - si + 1) * this.dayWidth);
      return { left, width };
    },
    barFor(t) {
      // Ticket bar: created_at -> due_date. Falls back to a single-day marker.
      return this.barBetween(t.created_at, t.due_date) || { left: 0, width: this.dayWidth };
    },
    barColor(t) {
      if (this.colorMode === 'priority') {
        return PRIORITY_COLORS[(t.priority || '').toLowerCase()] || '#9aa0aa';
      }
      return STATUS_COLORS[t.status] || '#9aa0aa';
    },
    sprintColor(status) {
      if (status === 'active') return '#2e9e5b';
      if (status === 'completed') return '#9aa0aa';
      return '#2c7be5';
    },
    isOverdue(t) {
      if (!t || !t.due_date) return false;
      if (t.status === 'Done' || t.status === 'Cancelled') return false;
      const due = this.toDate(t.due_date);
      if (!due) return false;
      return due < this.midnight(new Date());
    },
    assigneeText(t) {
      if (Array.isArray(t.assignees_details) && t.assignees_details.length) {
        return t.assignees_details.map((a) => a.fullname).join(', ');
      }
      return 'Unassigned';
    },
    ticketTitle(t) {
      const created = t.created_at ? this.formattedDate(t.created_at) : '—';
      const due = t.due_date ? this.formattedDate(t.due_date) : 'no due date';
      return `${t.ticket_id} — ${t.name}\n`
        + `Status: ${t.status} · Priority: ${t.priority || '—'}\n`
        + `${created} → ${due}${this.isOverdue(t) ? ' (OVERDUE)' : ''}\n`
        + `Assignees: ${this.assigneeText(t)}`;
    },
    baselineTitle() {
      if (!this.project) return '';
      const st = this.project.start_date ? this.formattedDate(this.project.start_date) : '—';
      const en = this.project.end_date ? this.formattedDate(this.project.end_date) : '—';
      return `Planned project timeline\n${st} → ${en}`;
    },
    sprintTitle(s) {
      const st = s.start_date ? this.formattedDate(s.start_date) : '—';
      const en = s.end_date ? this.formattedDate(s.end_date) : '—';
      return `${s.name} (${s.status})\n${st} → ${en}` + (s.goal ? `\nGoal: ${s.goal}` : '');
    },
    getProjects() {
      axios.get(utils._api(this.constant.list_projects), this.authHeaders())
        .then((response) => {
          this.projects = response.data.data || [];
          if (this.projects.length && !this.selectedProject) {
            this.selectedProject = this.projects[0].id;
            this.loadGantt();
          }
        })
        .catch((error) => console.error('Error fetching projects:', error));
    },
    loadGantt() {
      if (!this.selectedProject) return;
      this.loading = true;
      axios.post(utils._api(this.constant.sprint_gantt), { project_id: this.selectedProject }, this.authHeaders())
        .then((response) => {
          const data = response.data.data || {};
          this.project = data.project || null;
          this.sprints = data.sprints || [];
          this.tickets = data.tickets || [];
        })
        .catch((error) => {
          console.error('Error fetching gantt data:', error);
          this.notify('Failed to load Gantt data');
          this.project = null;
          this.sprints = [];
          this.tickets = [];
        })
        .finally(() => { this.loading = false; });
    },
  },
  mounted() {
    this.getProjects();
  },
};
</script>

<style scoped>
.gantt-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
}
.gantt-inner {
  position: relative;
  width: calc(var(--label) + var(--track));
  min-width: 100%;
}

/* Rows */
.g-row {
  display: flex;
  align-items: stretch;
  min-height: 34px;
}
.g-row.g-ticketrow { border-top: 1px solid var(--int-border); }
.g-row.g-sprintrow { border-top: 2px solid var(--int-border); background: var(--int-surface-muted); }

/* Sticky left label column */
.g-label {
  position: sticky;
  left: 0;
  z-index: 3;
  width: var(--label);
  min-width: var(--label);
  max-width: var(--label);
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: inherit;
  border-right: 1px solid var(--int-border);
  font-size: 0.8rem;
  overflow: hidden;
}
.g-sprintrow .g-label { background: var(--int-surface-muted); font-weight: 700; }
.g-ticketrow .g-label { background: var(--int-surface); }
.g-corner {
  background: var(--int-surface-muted);
  font-weight: 700;
  color: var(--int-text-muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.g-tid {
  font-weight: 700;
  color: var(--int-text-faint);
  font-size: 0.68rem;
  margin-right: 4px;
  white-space: nowrap;
}
.g-empty-sub { color: var(--int-text-faint); font-style: italic; font-weight: 400; }

/* Timeline track */
.g-track {
  position: relative;
  width: var(--track);
  min-width: var(--track);
  /* light weekly gridlines */
  background-image: repeating-linear-gradient(
    to right,
    transparent 0,
    transparent calc(var(--week) - 1px),
    var(--int-border) calc(var(--week) - 1px),
    var(--int-border) var(--week)
  );
}

/* Header */
.g-head { position: sticky; top: 0; z-index: 4; background: var(--int-surface); border-bottom: 1px solid var(--int-border); }
.g-monthbar { height: 30px; background-color: var(--int-surface); }
.g-month {
  position: absolute;
  top: 0;
  height: 30px;
  display: flex;
  align-items: center;
  padding-left: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--int-text-muted);
  border-left: 1px solid var(--int-border);
  white-space: nowrap;
}

/* Bars */
.g-bar {
  position: absolute;
  top: 5px;
  height: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
  overflow: hidden;
  cursor: default;
}
.g-bar-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.g-sprintbar { top: 5px; height: 22px; opacity: 0.95; border-radius: 6px; font-weight: 700; }
.g-overdue { outline: 2px solid #e5484d; outline-offset: -1px; }
.g-overdue-ic { margin-left: 4px; color: #fff; flex: 0 0 auto; }
.g-nodate {
  position: absolute;
  left: 8px;
  top: 8px;
  font-size: 0.72rem;
  color: var(--int-text-faint);
  font-style: italic;
}

/* Project baseline row */
.g-row.g-baselinerow { border-top: 2px solid var(--int-border); background: #fbf7e8; }
.g-baselinerow .g-label { background: #fbf7e8; font-weight: 800; }
.g-baselinelabel { color: var(--int-text-strong); }
.g-baselinebar {
  top: 6px;
  height: 20px;
  background: repeating-linear-gradient(45deg, #161616, #161616 6px, #333 6px, #333 12px);
  color: #fff;
  border-radius: 5px;
  opacity: 0.92;
}

/* Today marker */
.g-today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e5484d;
  z-index: 2;
  pointer-events: none;
}
.g-today-flag {
  position: absolute;
  top: 0;
  left: 2px;
  background: #e5484d;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 0 4px 4px 0;
  white-space: nowrap;
}

/* Target end (deadline) marker */
.g-target-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px dashed #7a5a00;
  z-index: 2;
  pointer-events: none;
}
.g-target-flag {
  position: absolute;
  top: 0;
  left: 2px;
  background: #7a5a00;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 0 4px 4px 0;
  white-space: nowrap;
}

/* Legend */
.g-legend { display: inline-flex; align-items: center; gap: 5px; font-size: 0.74rem; color: var(--int-text-muted); }
.g-legend-dot { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
</style>
